import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

function RecipePreview({ recipe }) {
  const data = typeof recipe.recipe === 'object' ? recipe.recipe : (recipe.recipe_data || {});
  const ing  = data.ingredients || [];
  const steps = data.steps || [];
  return (
    <div className="recipe-preview">
      <h4 className="recipe-preview-title">{recipe.title}</h4>
      {(data.servings || data.total_time_minutes) && (
        <div className="recipe-preview-meta">
          {data.servings && <span><i className="fas fa-users"></i> {data.servings} porcji</span>}
          {data.total_time_minutes && <span><i className="fas fa-clock"></i> {data.total_time_minutes} min</span>}
        </div>
      )}
      {ing.length > 0 && (
        <>
          <p className="recipe-preview-section">Składniki</p>
          <ul className="recipe-preview-list">
            {ing.map((x, i) => (
              <li key={i}>{x.amount} {x.unit} {x.name}</li>
            ))}
          </ul>
        </>
      )}
      {steps.length > 0 && (
        <>
          <p className="recipe-preview-section">Kroki</p>
          <ol className="recipe-preview-steps">
            {steps.map((s, i) => (
              <li key={i}>{typeof s === 'string' ? s : s.description || s.text || JSON.stringify(s)}</li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

const EMPTY_NEW = { username: '', email: '', password: '', is_admin: false };

export default function AdminPanel({ currentUserId, onClose, onLoadRecipe }) {
  const [stats, setStats]           = useState(null);
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  // Add-user modal
  const [showAdd, setShowAdd]       = useState(false);
  const [newUser, setNewUser]       = useState(EMPTY_NEW);
  const [addError, setAddError]     = useState('');

  // Edit modal
  const [editUser, setEditUser]     = useState(null);
  const [editError, setEditError]   = useState('');

  // Reset-password modal
  const [resetUser, setResetUser]   = useState(null);
  const [newPass, setNewPass]       = useState('');
  const [resetError, setResetError] = useState('');

  // Recipes modal
  const [importFile, setImportFile]   = useState(null); // { name, data }
  const [importMode, setImportMode]   = useState('merge');
  const [importing, setImporting]     = useState(false);
  const [exporting, setExporting]     = useState(false);

  const [recipesUser, setRecipesUser] = useState(null);
  const [recipesList, setRecipesList] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipePreview, setRecipePreview] = useState(null);
  const [deleteRecipeTarget, setDeleteRecipeTarget] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Inline limit edit
  const [editingLimitId, setEditingLimitId] = useState(null);
  const [limitDraft, setLimitDraft]         = useState('');

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([adminAPI.getStats(), adminAPI.listUsers()]);
      setStats(s.data);
      setUsers(u.data.users);
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd ładowania danych', true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Add user ──────────────────────────────────────────────────────────────────
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddError('');
    try {
      const r = await adminAPI.createUser(newUser);
      setUsers(prev => [r.data.user, ...prev]);
      setStats(prev => prev ? { ...prev, total_users: prev.total_users + 1, active_users: prev.active_users + 1, admin_users: newUser.is_admin ? prev.admin_users + 1 : prev.admin_users } : prev);
      setShowAdd(false);
      setNewUser(EMPTY_NEW);
      flash('Użytkownik utworzony');
    } catch (e) {
      setAddError(e.response?.data?.error || 'Błąd tworzenia użytkownika');
    }
  };

  // ── Edit user ─────────────────────────────────────────────────────────────────
  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditError('');
    try {
      const r = await adminAPI.updateUser(editUser.id, {
        email:     editUser.email,
        is_admin:  editUser.is_admin,
        is_active: editUser.is_active,
      });
      setUsers(prev => prev.map(u => u.id === editUser.id ? r.data.user : u));
      setEditUser(null);
      flash('Zapisano zmiany');
      load(); // refresh stats
    } catch (e) {
      setEditError(e.response?.data?.error || 'Błąd zapisu');
    }
  };

  // ── Reset password ────────────────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    setResetError('');
    try {
      await adminAPI.resetPassword(resetUser.id, newPass);
      setResetUser(null);
      setNewPass('');
      flash('Hasło zresetowane');
    } catch (e) {
      setResetError(e.response?.data?.error || 'Błąd resetowania hasła');
    }
  };

  // ── Delete user ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await adminAPI.deleteUser(deleteTarget.id);
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      setDeleteTarget(null);
      flash('Użytkownik usunięty');
      load();
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd usuwania', true);
      setDeleteTarget(null);
    }
  };

  // ── Inline limit save ─────────────────────────────────────────────────────────
  const saveLimitInline = async (userId) => {
    const val = limitDraft.trim();
    const monthly_limit = val === '' || val === '∞' ? null : parseInt(val, 10);
    if (monthly_limit !== null && (isNaN(monthly_limit) || monthly_limit < 1)) {
      flash('Podaj liczbę ≥ 1 lub pozostaw puste (bez limitu)', true);
      return;
    }
    try {
      const r = await adminAPI.updateUser(userId, { monthly_limit });
      setUsers(prev => prev.map(u => u.id === userId ? r.data.user : u));
      setEditingLimitId(null);
      flash('Limit zaktualizowany');
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd', true);
    }
  };

  // ── Reset conversions counter ─────────────────────────────────────────────────
  const resetConversions = async (user) => {
    try {
      const r = await adminAPI.resetConversions(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? r.data.user : u));
      flash(`Licznik konwersji dla "${user.username}" zresetowany`);
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd', true);
    }
  };

  // ── Quick toggle block ────────────────────────────────────────────────────────
  const toggleActive = async (user) => {
    try {
      const r = await adminAPI.updateUser(user.id, { is_active: !user.is_active });
      setUsers(prev => prev.map(u => u.id === user.id ? r.data.user : u));
      flash(r.data.user.is_active ? 'Konto odblokowane' : 'Konto zablokowane');
      load();
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd', true);
    }
  };

  // ── Eksport / import bazy przepisów ──────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await adminAPI.exportRecipes();
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matemeal-przepisy-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      flash(`Wyeksportowano ${r.data.count} przepisów`);
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd eksportu', true);
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (e) => {
    const f = e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (data.format !== 'matemeal-recipes' || !Array.isArray(data.recipes)) throw new Error();
        setImportMode('merge');
        setImportFile({ name: f.name, data });
      } catch {
        flash('Nieprawidłowy plik — wybierz wcześniejszy eksport przepisów', true);
      }
    };
    reader.readAsText(f);
  };

  const handleImportConfirm = async () => {
    if (!importFile || importing) return;
    setImporting(true);
    try {
      const r = await adminAPI.importRecipes(importFile.data, importMode);
      const d = r.data;
      flash(`Zaimportowano ${d.imported} przepisów` +
        (d.skipped_existing ? `, pominięto ${d.skipped_existing} już istniejących` : '') +
        (d.skipped_no_user ? `, ${d.skipped_no_user} bez pasującego użytkownika` : ''));
      setImportFile(null);
      load();
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd importu', true);
    } finally {
      setImporting(false);
    }
  };

  // ── User recipes ──────────────────────────────────────────────────────────────
  const openRecipes = async (user) => {
    setRecipesUser(user);
    setRecipesLoading(true);
    try {
      const r = await adminAPI.getUserRecipes(user.id);
      setRecipesList(r.data.recipes);
    } catch {
      setRecipesList([]);
    } finally {
      setRecipesLoading(false);
    }
  };

  const handleDeleteRecipe = async () => {
    if (!deleteRecipeTarget) return;
    try {
      const r = await adminAPI.deleteRecipe(deleteRecipeTarget.id);
      setRecipesList(prev => prev.filter(x => x.id !== deleteRecipeTarget.id));
      setUsers(prev => prev.map(u =>
        u.id === recipesUser.id ? { ...u, recipe_count: r.data.recipe_count } : u
      ));
      setStats(prev => prev ? { ...prev, total_recipes: prev.total_recipes - 1 } : prev);
      setDeleteRecipeTarget(null);
      if (recipePreview?.id === deleteRecipeTarget.id) setRecipePreview(null);
    } catch (e) {
      flash(e.response?.data?.error || 'Błąd usuwania przepisu', true);
      setDeleteRecipeTarget(null);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <i className="fas fa-shield-alt"></i>
            <span>Panel Administratora</span>
          </div>
          <button className="admin-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Flash messages */}
        {error   && <div className="admin-flash admin-flash-error"><i className="fas fa-exclamation-triangle"></i> {error}</div>}
        {success && <div className="admin-flash admin-flash-success"><i className="fas fa-check-circle"></i> {success}</div>}

        {loading ? (
          <div className="admin-loading"><div className="spinner"></div><p>Ładowanie...</p></div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="admin-stats">
                <div className="admin-stat"><span className="stat-val">{stats.total_users}</span><span className="stat-lbl">Użytkownicy</span></div>
                <div className="admin-stat admin-stat-green"><span className="stat-val">{stats.active_users}</span><span className="stat-lbl">Aktywni</span></div>
                <div className="admin-stat admin-stat-red"><span className="stat-val">{stats.blocked_users}</span><span className="stat-lbl">Zablokowani</span></div>
                <div className="admin-stat admin-stat-gold"><span className="stat-val">{stats.admin_users}</span><span className="stat-lbl">Adminowie</span></div>
                <div className="admin-stat admin-stat-blue"><span className="stat-val">{stats.total_recipes}</span><span className="stat-lbl">Przepisy</span></div>
              </div>
            )}

            {/* Toolbar */}
            <div className="admin-toolbar">
              <button className="btn-primary admin-add-btn" onClick={() => { setNewUser(EMPTY_NEW); setAddError(''); setShowAdd(true); }}>
                <i className="fas fa-user-plus"></i> Dodaj użytkownika
              </button>
              <button className="btn-secondary" onClick={load} title="Odśwież">
                <i className="fas fa-sync-alt"></i>
              </button>
              <span className="admin-toolbar-spacer"></span>
              <button className="btn-secondary" onClick={handleExport} disabled={exporting}
                title="Pobierz wszystkie przepisy użytkowników jako plik JSON">
                {exporting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-download"></i>} Eksport przepisów
              </button>
              <label className="btn-secondary admin-import-label" title="Wczytaj przepisy z wcześniejszego eksportu">
                <i className="fas fa-upload"></i> Import przepisów
                <input type="file" accept="application/json,.json" onChange={handleImportFile} style={{ display: 'none' }} />
              </label>
            </div>

            {/* Users table */}
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Użytkownik</th>
                    <th>Email / konto MC</th>
                    <th>Przepisy</th>
                    <th>Konwersje</th>
                    <th>Limit/mies.</th>
                    <th>Reset limitu</th>
                    <th>Rejestracja</th>
                    <th>Status</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => {
                    const isUnlimited = u.is_admin || u.monthly_limit === null;
                    const usagePct = isUnlimited ? 0 : Math.min(100, Math.round((u.conversions_count / u.monthly_limit) * 100));
                    const usageColor = usagePct >= 100 ? '#f87171' : usagePct >= 80 ? '#fbbf24' : '#4ade80';
                    return (
                    <tr key={u.id} className={!u.is_active ? 'row-blocked' : ''}>
                      <td className="td-id">{u.id}</td>
                      <td className="td-name">
                        {u.is_admin && <span className="badge-admin" title="Administrator"><i className="fas fa-shield-alt"></i></span>}
                        {u.username}
                        {u.id === currentUserId && <span className="badge-me"> (Ty)</span>}
                      </td>
                      <td className="td-email">
                        <div>{u.email || '—'}</div>
                        {u.mc_account_id && (
                          <div className="td-mc-id" title="Powiązane konto MC">
                            <i className="fas fa-link"></i> {u.mc_account_id}
                          </div>
                        )}
                      </td>
                      <td className="td-center">
                        <button className="btn-link" onClick={() => openRecipes(u)} title="Pokaż przepisy">
                          {u.recipe_count}
                        </button>
                      </td>
                      <td className="td-center">
                        {isUnlimited ? (
                          <span className="usage-unlimited" title="Bez limitu"><i className="fas fa-infinity"></i></span>
                        ) : (
                          <div className="usage-wrap">
                            <span className="usage-num" style={{ color: usageColor }}>{u.conversions_count}</span>
                            <div className="usage-bar-bg">
                              <div className="usage-bar-fill" style={{ width: `${usagePct}%`, background: usageColor }} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="td-center">
                        {editingLimitId === u.id ? (
                          <div className="limit-edit-inline">
                            <input
                              type="number" min="1" value={limitDraft}
                              onChange={e => setLimitDraft(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveLimitInline(u.id); if (e.key === 'Escape') setEditingLimitId(null); }}
                              placeholder="∞"
                              autoFocus
                            />
                            <button className="action-btn action-ok" onClick={() => saveLimitInline(u.id)} title="Zapisz"><i className="fas fa-check"></i></button>
                            <button className="action-btn" onClick={() => setEditingLimitId(null)} title="Anuluj"><i className="fas fa-times"></i></button>
                          </div>
                        ) : (
                          <button className="btn-link limit-badge" onClick={() => { setEditingLimitId(u.id); setLimitDraft(u.monthly_limit ?? ''); }}>
                            {isUnlimited ? <><i className="fas fa-infinity"></i></> : u.monthly_limit}
                          </button>
                        )}
                      </td>
                      <td className="td-date">
                        {u.limit_reset_at ? (
                          <div>
                            <div>{fmtDate(u.limit_reset_at).split(' ')[0]}</div>
                            <button className="btn-link reset-counter-btn" onClick={() => resetConversions(u)} title="Zresetuj licznik teraz">
                              <i className="fas fa-redo-alt"></i> reset
                            </button>
                          </div>
                        ) : (
                          isUnlimited ? '—' : (
                            <button className="btn-link reset-counter-btn" onClick={() => resetConversions(u)}>
                              <i className="fas fa-redo-alt"></i> ustaw
                            </button>
                          )
                        )}
                      </td>
                      <td className="td-date">{fmtDate(u.created_at)}</td>
                      <td className="td-center">
                        <span className={`badge-status ${u.is_active ? 'badge-active' : 'badge-blocked'}`}>
                          {u.is_active ? 'Aktywny' : 'Zablokowany'}
                        </span>
                      </td>
                      <td className="td-actions">
                        <button className="action-btn" title="Edytuj" onClick={() => { setEditError(''); setEditUser({ ...u }); }}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="action-btn" title="Resetuj hasło" onClick={() => { setResetError(''); setNewPass(''); setResetUser(u); }}>
                          <i className="fas fa-key"></i>
                        </button>
                        <button
                          className={`action-btn ${u.is_active ? 'action-warn' : 'action-ok'}`}
                          title={u.is_active ? 'Zablokuj konto' : 'Odblokuj konto'}
                          onClick={() => toggleActive(u)}
                          disabled={u.id === currentUserId}
                        >
                          <i className={`fas ${u.is_active ? 'fa-ban' : 'fa-unlock'}`}></i>
                        </button>
                        <button
                          className="action-btn action-danger"
                          title="Usuń użytkownika"
                          onClick={() => setDeleteTarget(u)}
                          disabled={u.id === currentUserId}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Modal: Dodaj użytkownika ── */}
      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-user-plus"></i> Nowy użytkownik</h3>
              <button onClick={() => setShowAdd(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="admin-modal-body">
                {addError && <div className="admin-flash admin-flash-error">{addError}</div>}
                <div className="form-group">
                  <label>Nazwa użytkownika *</label>
                  <input type="text" value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Hasło * (min. 6 znaków)</label>
                  <input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} required minLength={6} />
                </div>
                <div className="form-group form-group-check">
                  <label>
                    <input type="checkbox" checked={newUser.is_admin} onChange={e => setNewUser(p => ({ ...p, is_admin: e.target.checked }))} />
                    Uprawnienia administratora
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="submit" className="btn-primary">Utwórz</button>
                <button type="button" className="btn-secondary" onClick={() => setShowAdd(false)}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Edytuj użytkownika ── */}
      {editUser && (
        <div className="admin-modal-overlay" onClick={() => setEditUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-edit"></i> Edytuj: {editUser.username}</h3>
              <button onClick={() => setEditUser(null)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleEditSave}>
              <div className="admin-modal-body">
                {editError && <div className="admin-flash admin-flash-error">{editError}</div>}
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={editUser.email || ''} onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="form-group form-group-check">
                  <label>
                    <input type="checkbox" checked={editUser.is_admin} onChange={e => setEditUser(p => ({ ...p, is_admin: e.target.checked }))} disabled={editUser.id === currentUserId} />
                    Uprawnienia administratora
                  </label>
                </div>
                <div className="form-group form-group-check">
                  <label>
                    <input type="checkbox" checked={editUser.is_active} onChange={e => setEditUser(p => ({ ...p, is_active: e.target.checked }))} disabled={editUser.id === currentUserId} />
                    Konto aktywne
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="submit" className="btn-primary">Zapisz</button>
                <button type="button" className="btn-secondary" onClick={() => setEditUser(null)}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Reset hasła ── */}
      {resetUser && (
        <div className="admin-modal-overlay" onClick={() => setResetUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-key"></i> Reset hasła: {resetUser.username}</h3>
              <button onClick={() => setResetUser(null)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleReset}>
              <div className="admin-modal-body">
                {resetError && <div className="admin-flash admin-flash-error">{resetError}</div>}
                <div className="form-group">
                  <label>Nowe hasło * (min. 6 znaków)</label>
                  <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} required minLength={6} autoFocus />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="submit" className="btn-primary">Ustaw hasło</button>
                <button type="button" className="btn-secondary" onClick={() => setResetUser(null)}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Potwierdzenie usunięcia ── */}
      {deleteTarget && (
        <div className="admin-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="admin-modal admin-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-exclamation-triangle"></i> Usuń użytkownika</h3>
              <button onClick={() => setDeleteTarget(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="admin-modal-body">
              <p>Czy na pewno chcesz usunąć użytkownika <strong>{deleteTarget.username}</strong>?</p>
              <p className="confirm-warning">Ta operacja jest nieodwracalna. Usunięte zostaną również wszystkie przepisy tego użytkownika ({deleteTarget.recipe_count}).</p>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-danger" onClick={handleDelete}>
                <i className="fas fa-trash"></i> Usuń
              </button>
              <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Przepisy użytkownika ── */}
      {recipesUser && (
        <div className="admin-modal-overlay" onClick={() => { setRecipesUser(null); setRecipePreview(null); }}>
          <div className="admin-modal admin-modal-recipes" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-book"></i> Przepisy: {recipesUser.username}</h3>
              <button onClick={() => { setRecipesUser(null); setRecipePreview(null); }}><i className="fas fa-times"></i></button>
            </div>
            <div className="admin-modal-body admin-modal-body-split">
              <div className="recipes-list-col">
                {recipesLoading ? (
                  <div className="admin-loading"><div className="spinner"></div></div>
                ) : recipesList.length === 0 ? (
                  <p className="admin-empty">Brak zapisanych przepisów.</p>
                ) : (
                  <div className="recipes-list">
                    {recipesList.map(r => (
                      <div key={r.id} className={`recipe-row ${recipePreview?.id === r.id ? 'recipe-row-active' : ''}`}>
                        <div className="recipe-row-title">{r.title}</div>
                        <div className="recipe-row-meta">
                          {fmtDate(r.created_at)}
                          {r.mc_url && <a href={r.mc_url} target="_blank" rel="noreferrer" className="recipe-row-link"><i className="fas fa-external-link-alt"></i> MC</a>}
                        </div>
                        <div className="recipe-row-actions">
                          <button className="action-btn" title="Podgląd" onClick={() => setRecipePreview(recipePreview?.id === r.id ? null : r)}>
                            <i className="fas fa-eye"></i>
                          </button>
                          {onLoadRecipe && (
                            <button className="action-btn" title="Wczytaj do mojego konwertera (możesz potem wysłać na swoje konto MC)"
                              onClick={() => { onLoadRecipe(r); onClose(); }}>
                              <i className="fas fa-file-import"></i>
                            </button>
                          )}
                          <button className="action-btn action-danger" title="Usuń przepis" onClick={() => setDeleteRecipeTarget(r)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {recipePreview && (
                <div className="recipe-preview-col">
                  <RecipePreview recipe={recipePreview} />
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="btn-secondary" onClick={() => { setRecipesUser(null); setRecipePreview(null); }}>Zamknij</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Import przepisów z pliku ── */}
      {importFile && (
        <div className="admin-modal-overlay" onClick={() => !importing && setImportFile(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-upload"></i> Import przepisów</h3>
              <button onClick={() => !importing && setImportFile(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="admin-modal-body">
              <p>Plik <strong>{importFile.name}</strong> zawiera <strong>{importFile.data.count ?? importFile.data.recipes.length}</strong> przepisów
                {importFile.data.exported_at && <> (eksport z {fmtDate(importFile.data.exported_at)})</>}.</p>
              <label className="checkbox-label" style={{ marginTop: '0.75rem' }}>
                <input type="radio" name="import-mode" checked={importMode === 'merge'} onChange={() => setImportMode('merge')} />
                <span><strong>Dopisz brakujące</strong> — istniejące przepisy zostają, duplikaty są pomijane</span>
              </label>
              <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                <input type="radio" name="import-mode" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} />
                <span style={{ color: '#f87171' }}><strong>Zastąp całą bazę</strong> — wszystkie obecne przepisy zostaną usunięte i zastąpione plikiem</span>
              </label>
              <p className="admin-empty" style={{ marginTop: '0.75rem' }}>
                Przepisy są przypisywane do użytkowników po adresie email — wpisy nieistniejących już kont zostaną pominięte.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className={importMode === 'replace' ? 'btn-danger' : 'btn-primary'} onClick={handleImportConfirm} disabled={importing}>
                {importing ? <><i className="fas fa-spinner fa-spin"></i> Importowanie...</> : (importMode === 'replace' ? 'Zastąp bazę' : 'Importuj')}
              </button>
              <button className="btn-secondary" onClick={() => setImportFile(null)} disabled={importing}>Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Potwierdzenie usunięcia przepisu ── */}
      {deleteRecipeTarget && (
        <div className="admin-modal-overlay" onClick={() => setDeleteRecipeTarget(null)}>
          <div className="admin-modal admin-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><i className="fas fa-exclamation-triangle"></i> Usuń przepis</h3>
              <button onClick={() => setDeleteRecipeTarget(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="admin-modal-body">
              <p>Czy na pewno chcesz usunąć przepis <strong>{deleteRecipeTarget.title}</strong>?</p>
              <p className="confirm-warning">Ta operacja jest nieodwracalna.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-danger" onClick={handleDeleteRecipe}>
                <i className="fas fa-trash"></i> Usuń
              </button>
              <button className="btn-secondary" onClick={() => setDeleteRecipeTarget(null)}>Anuluj</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
